import { useOnlineStatus } from '@tunasong/models'
import { profilesApi, useSelector } from '@tunasong/redux'
import { FullPageProgress } from '@tunasong/ui-lib'
import type { FC, ReactNode } from 'react'
import { useState } from 'react'
import { ProfileNameStep } from './profile-name-step.js'

/** pending: the profile is not known yet. ask: show the name step. pass: show the app for the rest of the session. */
type Gate = 'pending' | 'ask' | 'pass'

const nextGate = (
  current: Gate,
  { online, isError, profile }: { online: boolean; isError: boolean; profile?: { name?: string } }
): Gate => {
  if (current === 'pass') {
    return 'pass'
  }
  /**
   * Only a successful save ends the step, never the cache (the update mutation patches it optimistically), and never
   * a network drop: a failed save shows its inline error and the user retries.
   */
  if (current === 'ask') {
    return 'ask'
  }
  /** Offline or a failed load before the step shows never blocks: it could not save. Ask again next session. */
  if (!online || isError) {
    return 'pass'
  }
  if (!profile) {
    return 'pending'
  }
  return profile.name?.trim() ? 'pass' : 'ask'
}

export interface ProfileNameGateProps {
  children: ReactNode
}

/**
 * Every signed-in user has a profile name (docs/ux/2026-10-17-profile-name-required-ux.md). Rendered by AuthGuard once
 * the session is established. The step replaces the app at the current URL, so a deep link is kept.
 */
export const ProfileNameGate: FC<ProfileNameGateProps> = props => {
  const { children } = props
  const userId = useSelector(state => state.user.userId)
  const online = useOnlineStatus()
  const { data: profile, isError } = profilesApi.useLoadProfileQuery({ emailOrUserId: userId ?? '' }, { skip: !userId })
  const [gate, setGate] = useState<Gate>('pending')

  const next = nextGate(gate, { online, isError: isError || !userId, profile })
  if (next !== gate) {
    setGate(next)
  }

  if (next === 'pending') {
    return <FullPageProgress />
  }
  if (next === 'ask' && profile) {
    return <ProfileNameStep profileId={profile.id} onSaved={() => setGate('pass')} />
  }
  return <>{children}</>
}
